import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import "./update-stockV.css";
import { fetchProducts } from "../Redux/Actions/productVActions";

const UpdateStockV = ({ onUpdate }) => {
  const dispatch = useDispatch();
  const productsData = useSelector((state) => state.productVReducer?.products || []);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedSubProduct, setSelectedSubProduct] = useState("");
  const [newQuantity, setNewQuantity] = useState("");
  const [currentQuantity, setCurrentQuantity] = useState(0);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [modificationDate, setModificationDate] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [loading, setLoading] = useState(true);

  // Charger les produits
  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        await dispatch(fetchProducts());
      } catch (error) {
        console.error("Erreur chargement produits:", error);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [dispatch]);

  // Charger les localisations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await axios.get("/api/Locations/getAllLocations");
        setLocations(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Erreur chargement localisations :", err);
        setLocations([]);
      }
    };
    fetchLocations();
  }, []);

  // Charger le stock actuel
  useEffect(() => {
    const fetchCurrentStock = async () => {
      if (!selectedSubProduct && !selectedProduct) {
        setCurrentQuantity(0);
        setCurrentPrice(0);
        return;
      }

      try {
        const productId = selectedSubProduct || selectedProduct;
        console.log(`🔄 Chargement stock pour: ${productId}`);
        
        const response = await axios.get(`/api/StockV/getOneStock/${productId}`);
        console.log("📦 Réponse stock:", response.data);
        
        setCurrentQuantity(response.data.quantity || response.data.totalQuantity || 0);
        
        let price = response.data.averagePrice || 0;
        if (!price && response.data.subProductV && response.data.subProductV.price) {
          price = response.data.subProductV.price;
        }
        setCurrentPrice(price);
        
      } catch (error) {
        console.error("❌ Erreur récupération stock actuel:", error);
        setCurrentQuantity(0);
        setCurrentPrice(0);
      }
    };

    fetchCurrentStock();
  }, [selectedProduct, selectedSubProduct]);

  // Filtrer les produits par localisation
  const filteredProducts = selectedLocation
    ? productsData.filter(product => {
        if (!product.subProductsV || product.subProductsV.length === 0) return false;
        
        return product.subProductsV.some(sp => {
          const spLocationId = sp.localisation?._id || sp.localisation;
          return String(spLocationId) === String(selectedLocation);
        });
      })
    : productsData;

  const handleUpdate = async () => {
    const updateDate = new Date();

    if ((!selectedProduct && !selectedSubProduct) || !newQuantity) {
      alert("Veuillez sélectionner un produit/sous-produit et saisir une quantité");
      return;
    }

    if (parseFloat(newQuantity) < 0) {
      alert("La quantité ne peut pas être négative");
      return;
    }

    try {
      const productId = selectedSubProduct || selectedProduct;
      const response = await axios.put("/api/StockV/updateStock", {
        productId: productId,
        totalQuantity: parseFloat(newQuantity),
        averagePrice: currentPrice
      });

      setModificationDate(updateDate);
      alert("Stock mis à jour avec succès !");
      
      if (onUpdate) {
        onUpdate(response.data);
      }

      const stockResponse = await axios.get(`/api/StockV/getOneStock/${productId}`);
      setCurrentQuantity(stockResponse.data.quantity || stockResponse.data.totalQuantity || 0);
      setNewQuantity("");

    } catch (error) {
      console.error("❌ Erreur mise à jour stock:", error);
      alert("Erreur lors de la mise à jour du stock");
    }
  };

  const difference = newQuantity !== "" 
    ? parseFloat(newQuantity) - currentQuantity 
    : 0;

  // Obtenir les sous-produits du produit sélectionné
  const getSubProducts = () => {
    if (!selectedProduct) return [];
    const product = filteredProducts.find(p => p._id === selectedProduct);
    return product?.subProductsV || [];
  };

  if (loading) {
    return (
      <div className="update-stock-container">
        <div className="loading">Chargement des produits...</div>
      </div>
    );
  }

  return (
    <div className="update-stock-container">
      <h2>Mise à jour du Stock - Produits & Sous-produits</h2>

      {/* Filtre par localisation */}
      <div className="stock-form-group">
        <label>Localisation</label>
        <select
          value={selectedLocation}
          onChange={(e) => {
            setSelectedLocation(e.target.value);
            setSelectedProduct("");
            setSelectedSubProduct("");
          }}
        >
          <option value="">Toutes les localisations</option>
          {locations.map((location) => (
            <option key={location._id} value={location._id}>
              {location.name}
            </option>
          ))}
        </select>
      </div>

      {/* Sélection du produit */}
      <div className="stock-form-group">
        <label>Produit</label>
        <select
          value={selectedProduct}
          onChange={(e) => {
            setSelectedProduct(e.target.value);
            setSelectedSubProduct("");
          }}
        >
          <option value="">Choisir un produit</option>
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <option key={product._id} value={product._id}>
                {product.name} ({product.category})
              </option>
            ))
          ) : (
            <option value="" disabled>Aucun produit trouvé pour cette localisation</option>
          )}
        </select>
        {filteredProducts.length === 0 && selectedLocation && (
          <div className="warning-message">
            Aucun produit disponible pour cette localisation
          </div>
        )}
      </div>

      {/* Sélection du sous-produit */}
      <div className="stock-form-group">
        <label>Sous-produit</label>
        <select
          value={selectedSubProduct}
          onChange={(e) => setSelectedSubProduct(e.target.value)}
          disabled={!selectedProduct}
        >
          <option value="">Choisir un sous-produit (optionnel)</option>
          {getSubProducts().map((subProduct) => (
            <option key={subProduct._id} value={subProduct._id}>
              {subProduct.name} - {subProduct.price} DT
            </option>
          ))}
        </select>
        {selectedProduct && getSubProducts().length === 0 && (
          <div className="info-message">
            Ce produit n'a pas de sous-produits
          </div>
        )}
      </div>

      <div className="stock-form-group">
        <label>Quantité Actuelle</label>
        <input type="text" readOnly value={currentQuantity.toFixed(3)} />
        {currentQuantity === 0 && (selectedProduct || selectedSubProduct) && (
          <div className="info-message">
            Aucun stock enregistré
          </div>
        )}
      </div>

      <div className="stock-form-group">
        <label>Nouvelle Quantité</label>
        <input 
          type="number" 
          step="0.001" 
          min="0"
          value={newQuantity} 
          onChange={(e) => setNewQuantity(e.target.value)} 
          placeholder="Saisir la nouvelle quantité"
        />
      </div>

      <div className="stock-form-group">
        <label>Différence</label>
        <input 
          type="text" 
          readOnly 
          value={difference.toFixed(3)} 
          style={{ 
            color: difference < 0 ? 'red' : difference > 0 ? 'green' : 'black',
            fontWeight: 'bold' 
          }} 
        />
      </div>

      <div className="stock-form-group">
        <label>Prix Moyen Pondéré</label>
        <input type="text" readOnly value={`${currentPrice.toFixed(3)} DT`} />
      </div>

      <div className="stock-form-group">
        <label>Date de Modification</label>
        <input 
          type="text" 
          readOnly 
          value={modificationDate ? 
            modificationDate.toLocaleDateString() + ' ' + modificationDate.toLocaleTimeString() : 
            'Non modifié'
          } 
        />
      </div>

      <button 
        onClick={handleUpdate} 
        disabled={(!selectedProduct && !selectedSubProduct) || newQuantity === "" || parseFloat(newQuantity) < 0}
      >
        Mettre à jour le Stock
      </button>
    </div>
  );
};

export default UpdateStockV;